// frontend/src/pages/Login.js

import React, { memo, useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE_URL } from "../api";
import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";
import {
  Check,
  Heart,
  UsersThree,
  Sparkle,
} from "phosphor-react";

import ExplorePreview from "./ExplorePreview";
import PreLoginHeader from "../components/PreLoginHeader";

const logAxiosError = (err, ctx = "[Login]") => {
  try {
    const info = {
      message: err?.message,
      name: err?.name,
      status: err?.response?.status,
      statusText: err?.response?.statusText,
      data: err?.response?.data,
      headers: err?.response?.headers,
      method: err?.config?.method,
      baseURL: err?.config?.baseURL,
      url: err?.config?.url,
      timeout: err?.config?.timeout,
    };

    // eslint-disable-next-line no-console
    console.log(`${ctx} axios error ->`, info);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log("[Login] failed to log error", e);
  }
};

const OceanSparkleBackground = memo(function OceanSparkleBackground() {
  return (
    <div aria-hidden="true" className="ocean-background">
      <div className="ocean-light" />

      <div className="ocean-wave ocean-wave-one" />
      <div className="ocean-wave ocean-wave-two" />
      <div className="ocean-wave ocean-wave-three" />

      <div className="ocean-clouds">
        <div className="ocean-cloud ocean-cloud-one" />
        <div className="ocean-cloud ocean-cloud-two" />
        <div className="ocean-cloud ocean-cloud-three" />
        <div className="ocean-cloud ocean-cloud-four" />
        <div className="ocean-cloud ocean-cloud-five" />
      </div>

      <div className="ocean-wash" />
    </div>
  );
});

// Desktop-only copy for the left half of the hero.
const DesktopBackgroundText = memo(function DesktopBackgroundText() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none hidden w-full max-w-[520px] select-none text-right lg:block"
    >
      <p className="text-[46px] font-black leading-[0.82] tracking-[-0.055em] text-blue-100">
        a social diary
        <br />
        for your every day
      </p>

      <div className="mt-6 inline-flex flex-col items-start space-y-0 text-[21px] font-bold leading-tight tracking-[-0.039em] text-blue-100">
        <p className="flex items-center gap-2">
          <Heart
            size={20}
            weight="fill"
            className="shrink-0 text-blue-500"
          />
          <span>do more of what you love</span>
        </p>

        <p className="flex items-center gap-2">
          <UsersThree
            size={21}
            weight="fill"
            className="shrink-0 text-blue-500"
          />
          <span>find your community</span>
        </p>

        <p className="flex items-center gap-2">
          <Sparkle
            size={20}
            weight="fill"
            className="shrink-0 text-blue-500"
          />
          <span>celebrate your every step</span>
        </p>
      </div>
    </div>
  );
});

function Login() {
  // Uncontrolled inputs: typing no longer triggers a Login re-render at all.
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Only flips true/false (not on every keystroke), so re-renders are rare.
  const [canSubmit, setCanSubmit] = useState(false);

  const navigate = useNavigate();

  const checkCanSubmit = useCallback(() => {
    const hasEmail = Boolean(emailRef.current?.value.trim());
    const hasPassword = Boolean(passwordRef.current?.value.trim());

    setCanSubmit((prev) => {
      const next = hasEmail && hasPassword;
      return prev === next ? prev : next;
    });
  }, []);

  const toggleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const email = emailRef.current?.value.trim() || "";
    const password = passwordRef.current?.value || "";

    try {
      console.log("[Login] starting submit", {
        platform: Capacitor?.getPlatform?.(),
        API_BASE_URL,
        fullLoginURL: `${API_BASE_URL}/auth/login`,
        hasEmail: Boolean(email),
      });
    } catch {}

    try {
      const body = new URLSearchParams();
      body.set("username", email);
      body.set("password", password);

      const { data } = await api.post("/auth/login", body, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        timeout: 20000,
      });

      console.log("[Login] /auth/login response", {
        keys: Object.keys(data || {}),
        tokenType: data?.token_type,
        hasToken: Boolean(data?.access_token),
      });

      const token = data?.access_token;
      const tokenType = (data?.token_type || "Bearer").trim();

      if (!token) {
        setError(data?.detail || "Login failed");
        setLoading(false);
        return;
      }

      localStorage.setItem("token", token);

      try {
        await Preferences.set({
          key: "auth_token",
          value: token,
        });
      } catch (e) {
        console.warn(
          "[Login] Preferences.set failed, using web storage only:",
          e?.message || e
        );
      }

      let userId = data?.user?.id;

      if (!userId) {
        try {
          const meRes = await api.get("/users/me", {
            headers: {
              Authorization: `${tokenType} ${token}`,
            },
          });

          userId = meRes?.data?.id;

          console.log("[Login] /users/me response", {
            id: userId,
            ok: Boolean(userId),
          });
        } catch (meErr) {
          logAxiosError(meErr, "[Login]/users/me");
        }
      }

      if (userId) {
        localStorage.setItem("user_id", String(userId));
      }

      navigate("/home");
    } catch (err) {
      logAxiosError(err, "[Login]/auth/login");

      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Network or server error";

      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-blue-50 text-[#181411]">
      {/* LOGIN HERO */}
      <section className="relative min-h-screen overflow-hidden">
        <OceanSparkleBackground />
        <PreLoginHeader />

        <div className="relative z-10 flex min-h-screen items-center px-6 pb-14 pt-24 sm:pb-10 lg:px-0">
          <div className="w-full lg:grid lg:grid-cols-2">

            {/* LEFT HALF — RIGHT ALIGNED AGAINST CENTER */}
            <div className="hidden items-center justify-end lg:flex">
              <div className="w-full max-w-[520px] pr-6 text-right xl:pr-8">
                <DesktopBackgroundText />
              </div>
            </div>

            {/* RIGHT HALF — LEFT ALIGNED AGAINST CENTER */}
            <div className="flex w-full items-center justify-center lg:justify-start">
              <main className="flex w-full max-w-[400px] flex-col items-center lg:ml-6 xl:ml-8">

                <h1 className="logo-font mb-8 select-none text-[60px] font-black leading-[0.9] tracking-[-0.06em] text-blue-500 drop-shadow-[0_2px_14px_rgba(255,255,255,0.85)] sm:text-[72px]">
                  third place
                </h1>

                {/* MOBILE ONLY — SOCIAL DIARY COPY */}
                <div
                  aria-hidden="true"
                  className="mb-8 w-full select-none text-center lg:hidden"
                >
                  <p className="text-[22px] font-black leading-[0.88] tracking-[-0.05em] text-blue-500">
                    a social diary
                    <br />
                    for your every day
                  </p>

                  <div className="mt-3 inline-flex flex-col items-start space-y-0 text-[16px] font-bold leading-tight tracking-[-0.039em] text-blue-100">
                    <p className="flex items-center gap-1.5">
                      <Heart size={15} weight="fill" className="text-blue-500"/>
                      <span>do more of what you love</span>
                    </p>

                    <p className="flex items-center gap-1.5">
                      <UsersThree size={16} weight="fill" className="text-blue-500" />
                      <span>find your community</span>
                    </p>

                    <p className="flex items-center gap-1.5">
                      <Sparkle size={15} weight="fill" className="text-blue-500" />
                      <span>celebrate your every step</span>
                    </p>
                  </div>
                </div>


                <div className="w-full max-w-[390px] -mt-1 rounded-[32px] bg-slate-50/80 py-5 px-4 sm:p-5 shadow-[0_24px_70px_rgba(22,59,86,0.04)]">
                  <form className="space-y-2" onSubmit={handleSubmit}>
                    {error && (
                      <p
                        className="font-medium rounded-2xl !min-h-0 !min-w-0 px-3 text-left text-sm text-red-600"
                        aria-live="polite"
                      >
                        {error}
                      </p>
                    )}

                    <div>
                      <label className="mb-1.5 ml-3 block text-sm font-semibold text-[#6f665d]">
                        Email
                      </label>

                      <input
                        type="email"
                        required
                        ref={emailRef}
                        onInput={checkCanSubmit}
                        onChange={checkCanSubmit}
                        onFocus={checkCanSubmit}
                        autoComplete="username"
                        name="username"
                        inputMode="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        placeholder="you@example.com"
                        className="w-full rounded-3xl border border-white/80 bg-blue-50/85 !min-h-0 !min-w-0 px-3 py-1.5 text-sm outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 ml-3 block text-sm font-semibold text-[#6f665d]">
                        Password
                      </label>

                      <input
                        type={showPassword ? "text" : "password"}
                        required
                        ref={passwordRef}
                        onInput={checkCanSubmit}
                        onChange={checkCanSubmit}
                        onFocus={checkCanSubmit}
                        autoComplete="current-password"
                        name="current-password"
                        placeholder="••••••••"
                        className="w-full rounded-3xl border border-white/80 bg-blue-50/85 !min-h-0 !min-w-0 px-3 py-1.5 text-sm outline-none transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15"
                      />
                    </div>

                    <label className="inline-flex cursor-pointer items-center px-2 text-sm text-[#6f665d]">
                      <input
                        type="checkbox"
                        checked={showPassword}
                        onChange={toggleShowPassword}
                        className="peer sr-only"
                      />

                      <div
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-slate-200 transition-colors ${
                          showPassword
                            ? "border-blue-500 bg-blue-500"
                            : "bg-blue-50"
                        }`}
                      >
                        <Check
                          size={12}
                          weight="bold"
                          className={`text-white transition-opacity ${
                            showPassword ? "opacity-100" : "opacity-0"
                          }`}
                        />
                      </div>

                      <span className="ml-2">Show password</span>
                    </label>

                    <button
                      type="submit"
                      disabled={loading || !canSubmit}
                      className="
                        w-full transform-gpu rounded-full bg-blue-600 !min-w-0 !min-h-0 !py-3
                        text-sm font-semibold text-white
                        transition-all duration-300 ease-out
                        hover:scale-[1.03]
                        hover:bg-blue-700
                        active:scale-[0.99]
                        disabled:cursor-not-allowed
                        disabled:hover:scale-100
                      "
                    >
                      {loading ? "signing in…" : "Let's go!"}
                    </button>

                    <div className="text-center text-xs text-[#8a8078] !mt-4">
                      <button
                        type="button"
                        onClick={() => navigate("/signup")}
                        className="!min-h-0 !min-w-0 font-semibold text-blue-400 transition hover:font-bold"
                      >
                        Sign up
                      </button>

                      <button
                        type="button"
                        onClick={() => navigate("/forgotpassword")}
                        className="ml-3 !min-h-0 !min-w-0 font-medium text-blue-400 transition hover:font-bold"
                      >
                        Forgot password?
                      </button>
                    </div>
                  </form>
                </div>
              </main>
            </div>

          </div>
        </div>
      </section>

      <ExplorePreview embedded />
    </div>
  );
}

export default Login;